import {
   CALENDAR_EVENT_TONE_SET,
   calendarEventColorToCss,
   normalizeCalendarEventColorToken,
   resolveCalendarComboColorKey,
} from "../../../constants/calendarEventColors";

export function normalizeColorAliasToEventToken(value) {
   const raw = String(value || "").trim().toLowerCase();
   if (!raw) return "";

   if (
      raw === "default" ||
      raw === "--default" ||
      raw === "event-default" ||
      raw === "--event-default"
   ) {
      return "--event-default";
   }

   if (
      raw === "black" ||
      raw === "--black" ||
      raw === "black-s" ||
      raw === "--black-s" ||
      raw === "black-t" ||
      raw === "--black-t" ||
      raw === "transparent"
   ) {
      return "--black-t";
   }

   const comboKey = resolveCalendarComboColorKey(value);
   if (comboKey) return `--${comboKey}`;

   let bare = raw;
   if (bare.startsWith("--")) bare = bare.slice(2);
   if (bare.startsWith("event-")) bare = bare.slice("event-".length);
   if (!bare) return "";
   if (!CALENDAR_EVENT_TONE_SET.has(bare)) return "";
   return `--event-${bare}`;
}

export function normalizeColorToCss(value) {
   const sharedCss = calendarEventColorToCss(value);
   if (sharedCss) return sharedCss;

   const raw = String(value || "").trim();
   if (!raw) return "";

   if (raw.startsWith("var(") && raw.endsWith(")")) {
      const inner = raw.slice(4, -1).trim();
      const mappedInner = normalizeColorAliasToEventToken(inner);
      if (mappedInner) {
         const mappedCss = calendarEventColorToCss(mappedInner);
         if (mappedCss) return mappedCss;
         return `var(${mappedInner})`;
      }
      return raw;
   }

   const mappedRaw = normalizeColorAliasToEventToken(raw);
   if (mappedRaw) {
      const mappedCss = calendarEventColorToCss(mappedRaw);
      if (mappedCss) return mappedCss;
      return `var(${mappedRaw})`;
   }

   if (raw.startsWith("--")) {
      const token = raw.toLowerCase();
      if (token === "--default") return "var(--event-default)";
      const cssFromToken = calendarEventColorToCss(raw);
      if (cssFromToken) return cssFromToken;
      return `var(${raw})`;
   }

   const lower = raw.toLowerCase();
   if (
      lower === "default" ||
      lower === "event-default" ||
      lower === "--event-default"
   ) {
      return "var(--event-default)";
   }

   if (raw.startsWith("#")) return raw;
   if (raw.startsWith("rgb(") || raw.startsWith("rgba(")) return raw;
   if (raw.startsWith("hsl(") || raw.startsWith("hsla(")) return raw;
   if (lower.startsWith("linear-gradient(")) return raw;
   return raw;
}

export function hasMeaningfulCalendarEventColor(value) {
   const raw = String(value ?? "").trim();
   if (!raw) return false;
   const lower = raw.toLowerCase();
   if (
      lower === "default" ||
      lower === "--default" ||
      lower === "--event-default" ||
      lower === "event-default" ||
      lower === "var(--event-default)"
   ) {
      return false;
   }
   return true;
}

export function resolveSectorColor(sectorSlug) {
   if (sectorSlug === "botanica") return "var(--event-blue)";
   if (sectorSlug === "ciocana") return "var(--event-pink)";
   if (sectorSlug === "buiucani") return "var(--event-green)";
   return "var(--event-red)";
}

export function resolveInstructorHeaderStyles({ profileColor, sectorSlug }) {
   const headerColor =
      normalizeColorToCss(profileColor) || resolveSectorColor(sectorSlug);

   return { headerColor };
}

export function pickUserProfileColor(userLike) {
   return (
      userLike?.profile?.color ||
      userLike?.profileColor ||
      userLike?.color ||
      userLike?.uiColor ||
      userLike?.profile?.uiColor ||
      ""
   );
}

export function normalizeColorForCreate(value) {
   const raw = String(value || "").trim();
   if (!raw) return "";
   const tokenMatch = raw.match(/^var\((--[^)]+)\)$/i);
   if (tokenMatch?.[1]) {
      // Remap display aliases (e.g. --black-s → --black-t) instead of
      // returning the CSS var inner token as-is.
      return normalizeCalendarEventColorToken(tokenMatch[1], "");
   }
   return normalizeCalendarEventColorToken(raw, "");
}

export {
   CALENDAR_EVENT_COLOR_GRID_COLUMNS,
   CALENDAR_EVENT_COLOR_HINTS,
   CALENDAR_EVENT_COLOR_LABELS,
   CALENDAR_EVENT_COLOR_TOKENS,
   calendarEventColorSwatchStyle,
   normalizeCalendarEventColorToken,
} from "../../../constants/calendarEventColors";
